/**
 * resolveHistoryPrefill.js
 *
 * Resolves the display string for a History module line item.
 *
 * Primary source: patientPrefill / informantPrefill on the history stage
 * question (written by the prefill pipeline or demo setup).
 *
 * Fallback: when those fields are still null (e.g. demo patients created
 * with flat answer maps that the server-side formatter could not read),
 * look up the raw answer from form instances and format it for display.
 */
import { normalizeModularAnswerValue } from '../../5_patientOverview/utils/buildModularPatientOverviewModel';
import { buildDisplayAnswer, normalizeRawAnswer, coerceDisplayText } from '../../../../utils/formDisplayUtils';
import { resolveFormInstanceForDisplay } from '../../../../utils/resolveFormTemplates';
import {
  refreshDobAge,
  replaceNoneOfTheAboveDisplay,
} from '../../../serviceUsers/3_forms/functions/PrefillFunctions';
import { formatHistoryPrefillFromFormInstances } from '../../../serviceUsers/3_forms/functions/AutismPrefillFunctions';

/**
 * Normalise stored prefill strings that may have been written by the simplified
 * demo server formatter (newline-joined composites, raw ISO dates).
 */
function finalizeHistoryPrefillDisplay(text) {
  if (text == null || text === '') return null;
  const coerced = typeof text === 'string' ? text : coerceDisplayText(text);
  return coerced ? replaceNoneOfTheAboveDisplay(coerced) : null;
}

function normalizeStoredHistoryPrefill(providerVarName, stored) {
  if (!stored) return null;

  // Some prefill writes store structured answers (arrays/objects) instead of
  // formatted strings — coerce before FormattedAnswer sees them.
  if (typeof stored !== 'string') {
    return finalizeHistoryPrefillDisplay(stored);
  }

  if (providerVarName === 'name' && stored.includes('\n')) {
    return stored
      .split('\n')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ');
  }

  // Date of birth: the stored value may be a raw ISO date (demo seeds) or
  // the formatted string the prefill pipeline writes at form submission,
  // "15th March 1989 (35 years old)". Either way the age was fixed at write
  // time, so it is recalculated here for today. Shared with the Adult ADHD
  // and CYP ADHD Clinical Interview pages so all three agree. A value that is
  // not a date passes through refreshDobAge unchanged and then gets the same
  // none-option normalisation every other stored string gets, exactly as
  // before.
  if (providerVarName === 'dateOfBirth') {
    return replaceNoneOfTheAboveDisplay(refreshDobAge(stored));
  }

  return replaceNoneOfTheAboveDisplay(stored);
}

/**
 * Locate a subquestion's raw answer and inputType across all form instances.
 * Supports both nested answers (questionKey -> varName) and flat demo answers
 * (varName at the top level of answers).
 *
 * @param {string} [patientFirstName] - Resolves {{patientFirstName}} in informant schemas
 */
function findAnswerInFormInstances(formInstances, varName, patientFirstName = '') {
  for (const formInstance of Object.values(formInstances || {})) {
    const displayInstance = patientFirstName
      ? resolveFormInstanceForDisplay(formInstance, patientFirstName)
      : formInstance;

    const formDef = displayInstance?.formDefinition;
    if (!formDef?.formSubsections) continue;

    const answers = formInstance.answers || {};

    for (const subsection of Object.values(formDef.formSubsections)) {
      for (const [qKey, question] of Object.entries(subsection.questions || {})) {
        for (const subquestion of Object.values(question.subquestions || {})) {
          if (subquestion.serviceUserVarName !== varName) continue;

          const nestedAnswer = answers[qKey]?.[varName];
          const raw = nestedAnswer !== undefined && nestedAnswer !== null
            ? nestedAnswer
            : answers[varName];

          if (raw === undefined || raw === null) return null;

          return {
            raw,
            inputType: subquestion.inputType,
            otherAnswer: subquestion.otherAnswer,
          };
        }
      }
    }
  }

  return null;
}

/**
 * Format a single varName lookup into a human-readable display string.
 */
function formatVarNameAnswer(formInstances, varName, patientFirstName = '') {
  const found = findAnswerInFormInstances(formInstances, varName, patientFirstName);
  if (!found) return null;

  const normalized = normalizeModularAnswerValue(found.raw, found.inputType);
  const display = buildDisplayAnswer(normalized, '');
  return display || null;
}

function getVarNamesForActor(question, actorType) {
  return actorType === 'patient'
    ? (question?.prefilledByServiceUserVarNames || [])
    : (question?.prefilledByInformantVarNames || []);
}

/**
 * Resolve the form subquestion inputType for a single-source history line item.
 * Used by FormattedAnswer to italic-quote standalone free-text fields.
 */
function resolveHistoryPrefillInputType(
  question,
  formInstances,
  actorType,
  patientFirstName = '',
) {
  const varNames = getVarNamesForActor(question, actorType);
  if (varNames.length !== 1) return null;

  const found = findAnswerInFormInstances(formInstances, varNames[0], patientFirstName);
  return found?.inputType ?? null;
}

/**
 * Resolve the raw form answer for FormattedAnswer rendering (italic quoted
 * free text).
 *
 * - Single-source questions: return the normalised raw answer (1:1 with the
 *   form subquestion), matching CIQuestionPanel.
 * - Multi-varName composites (e.g. Partnership): return only the structured
 *   answer item(s) that carry typed free text (`otherValue`). FormattedAnswer
 *   then italic-quotes that detail *inside* the labelled composite display
 *   string. Composites with no free-text elaborations still return null so
 *   display stays plain text-only.
 *
 * @returns {*|null}
 */
export function resolveHistoryPrefillRawAnswer(
  question,
  formInstances,
  actorType,
  patientFirstName = '',
) {
  const varNames = getVarNamesForActor(question, actorType);
  if (varNames.length === 0) return null;

  if (varNames.length === 1) {
    const found = findAnswerInFormInstances(formInstances, varNames[0], patientFirstName);
    if (!found) return null;

    const normalized = normalizeModularAnswerValue(found.raw, found.inputType);
    return normalizeRawAnswer(normalized, found.otherAnswer);
  }

  // Composite: collect free-text elaborations across source varNames so the
  // History panel can italic-quote typed details (e.g. Partnership
  // "Relationship challenges: Yes: …") without dropping the surrounding labels.
  const freeTextItems = [];
  varNames.forEach((varName) => {
    const found = findAnswerInFormInstances(formInstances, varName, patientFirstName);
    if (!found) return;

    const normalized = normalizeModularAnswerValue(found.raw, found.inputType);
    const answer = normalizeRawAnswer(normalized, found.otherAnswer);

    if (Array.isArray(answer)) {
      answer.forEach((item) => {
        if (
          item
          && typeof item === 'object'
          && item.value != null
          && item.otherValue != null
          && String(item.otherValue).trim() !== ''
        ) {
          freeTextItems.push(item);
        }
      });
    } else if (
      answer
      && typeof answer === 'object'
      && answer.value != null
      && answer.otherValue != null
      && String(answer.otherValue).trim() !== ''
    ) {
      freeTextItems.push(answer);
    }
  });

  if (freeTextItems.length === 0) return null;
  return freeTextItems.length === 1 ? freeTextItems[0] : freeTextItems;
}

/**
 * Resolve display text and raw answer together for a history line item.
 */
export function resolveHistoryPrefillDisplay(
  question,
  formInstances,
  actorType,
  patientFirstName = '',
) {
  const prefillField = actorType === 'patient' ? 'patientPrefill' : 'informantPrefill';
  const stored = question?.[prefillField];
  const displayText = stored
    ? normalizeStoredHistoryPrefill(question.providerVarName, stored)
    : resolveHistoryPrefillValue(question, formInstances, actorType, patientFirstName);

  const rawAnswer = resolveHistoryPrefillRawAnswer(
    question,
    formInstances,
    actorType,
    patientFirstName,
  );

  const inputType = resolveHistoryPrefillInputType(
    question,
    formInstances,
    actorType,
    patientFirstName,
  );

  return {
    displayText: finalizeHistoryPrefillDisplay(displayText),
    rawAnswer,
    inputType,
  };
}

/**
 * Resolve informant column display + raw answer for a patient history question.
 */
export function resolveInformantHistoryPrefillDisplay(
  patientQuestion,
  informantHistoryQuestion,
  informantFormInstances,
  patientFirstName = '',
) {
  const stored = informantHistoryQuestion?.informantPrefill;
  const displayText = stored
    ? normalizeStoredHistoryPrefill(patientQuestion.providerVarName, stored)
    : resolveHistoryPrefillValue(
      patientQuestion,
      informantFormInstances,
      'informant',
      patientFirstName,
    );

  const rawAnswer = resolveHistoryPrefillRawAnswer(
    patientQuestion,
    informantFormInstances,
    'informant',
    patientFirstName,
  );

  const inputType = resolveHistoryPrefillInputType(
    patientQuestion,
    informantFormInstances,
    'informant',
    patientFirstName,
  );

  return {
    displayText: finalizeHistoryPrefillDisplay(displayText),
    rawAnswer,
    inputType,
  };
}

/**
 * Resolve the display prefill for one history question.
 *
 * @param {object} question - History stage subsection question
 * @param {object} formInstances - formKey -> formInstance map for the actor
 * @param {'patient'|'informant'} actorType
 * @returns {string|null}
 */
export function resolveHistoryPrefillValue(
  question,
  formInstances,
  actorType,
  patientFirstName = '',
) {
  const prefillField = actorType === 'patient' ? 'patientPrefill' : 'informantPrefill';
  const varNamesField = actorType === 'patient'
    ? 'prefilledByServiceUserVarNames'
    : 'prefilledByInformantVarNames';

  const stored = question?.[prefillField];
  if (stored) {
    return normalizeStoredHistoryPrefill(question.providerVarName, stored);
  }

  const varNames = question?.[varNamesField] || [];
  if (varNames.length === 0) return null;

  const providerVarName = question?.providerVarName;

  // Constructed prefill — reuse ADHD/Autism PrefillFunctions handlers so multi-
  // question line items render as statements (e.g. "Alcohol consumption: Monthly or less").
  const constructed = formatHistoryPrefillFromFormInstances(
    providerVarName,
    formInstances,
    actorType,
    patientFirstName,
  );
  if (constructed) return replaceNoneOfTheAboveDisplay(constructed);

  const parts = varNames
    .map((varName) => formatVarNameAnswer(formInstances, varName, patientFirstName))
    .filter(Boolean);

  return parts.length > 0
    ? replaceNoneOfTheAboveDisplay(parts.join('\n'))
    : null;
}

/**
 * Resolve informant-column display text for a patient history question.
 *
 * Stored informantPrefill lives on the informant's history stage document
 * (a filtered subset). Var-name lookup uses the patient question's
 * prefilledByInformantVarNames plus the informant's form instances.
 *
 * @param {object} patientQuestion - Question from the patient's history stage
 * @param {object|null} informantHistoryQuestion - Matching question on informant history doc
 * @param {object} informantFormInstances - Informant formKey -> formInstance map
 * @returns {string|null}
 */
export function resolveInformantHistoryPrefill(
  patientQuestion,
  informantHistoryQuestion,
  informantFormInstances,
) {
  const stored = informantHistoryQuestion?.informantPrefill;
  if (stored) return finalizeHistoryPrefillDisplay(stored);

  return resolveHistoryPrefillValue(
    patientQuestion,
    informantFormInstances,
    'informant',
  );
}
